import { studyQueryKeys } from '@/config/queryKeys';
import { PLACEHOLDER_VALUE } from '@/constants';
import { useDateFormatter } from '@/hooks';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { PaginatedSampleProteoformsResponse, SampleProteoformPublic } from '@/types';
import { getGridItemClassName } from '@nautilusbio/nb-design-system';
import { Badge } from '@nautilusbio/nb-design-system/Badge';
import { Button } from '@nautilusbio/nb-design-system/Button';
import Loading from '@nautilusbio/nb-design-system/Loading';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { ColDef, GridOptions } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, {
  type MouseEvent,
  RefObject,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { PanelImperativeHandle } from 'react-resizable-panels';

import tableStyles from '../../../styles/components/table.module.css';
import irStyles from '../../Instrument_Runs/styles.module.css';
import styles from '../styles.module.css';
import {
  DATE_FILTER_PARAMS,
  TEXT_FILTER_PARAMS,
  createSetFilterParams,
  createSharedSampleColumns,
} from '../utils/sharedColumnDefs';

import { useSampleTableGrid } from './useSampleTableGrid';

interface SampleProteoformsTableProps {
  adjacentPanelRef: RefObject<PanelImperativeHandle | null>;
  orgId: string;
  panelRef: RefObject<PanelImperativeHandle | null>;
  studyId: string;
}

const localStorageKey = (studyId: string) => `study-${studyId}-proteoforms-agGridState`;
const paginationPageSizeKey = (studyId: string) => `study-${studyId}-proteoforms-pageSize`;

// Valid sort fields for this endpoint (per API ProteoformSortField enum)
const VALID_SORT_FIELDS = ['sample_name', 'instrument_run_name', 'proteoform_name', 'proteoform_abundance'];

/**
 * Grid options with server-side filtering, sorting, and compact layout
 * Uses AG Grid's built-in pagination size selector
 */
const gridOptions: GridOptions = {
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  suppressMovableColumns: false,
  suppressDragLeaveHidesColumns: false,
};

/**
 * SampleProteoformsTable Component
 *
 * Displays sample proteoform data with server-side filtering, sorting, and pagination.
 * Features:
 * - Column header menu filters (no floating filter row for clean UI)
 * - Set filters for categorical columns (instrument_run_name, well_position, demux_index)
 * - Text filters for free-form columns (sample_name, proteoform_name)
 * - Number filters for numeric columns (proteoform_abundance)
 * - Date filters for timestamp columns (created_at)
 * - Natural sorting for sample names with numeric components
 * - User-selectable rows per page via AG Grid's built-in pagination controls
 * - Server-side pagination for large datasets
 * - Fixed height scrollable container for consistent layout
 * - Compact visual layout with reduced padding
 * - Column state persistence
 *
 * @param props - Component props
 * @returns Rendered table with enhanced AG Grid filtering and pagination
 */
export const SampleProteoformsTable = forwardRef(function SampleProteoformsTable(
  { adjacentPanelRef, studyId, orgId, panelRef }: SampleProteoformsTableProps,
  ref,
) {
  const { t } = useTranslation('studies');
  const formatDate = useDateFormatter();
  const gridRef = useRef<AgGridReact<SampleProteoformPublic>>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const datasource = useMemo(
    () =>
      createAgGridDatasource<SampleProteoformPublic, PaginatedSampleProteoformsResponse>(
        studyQueryKeys.endpoints.proteoformsSearch(orgId, studyId),
        ({ skip, limit, sortBy, sortOrder, filters }) => {
          const validSortBy = sortBy && VALID_SORT_FIELDS.includes(sortBy) ? sortBy : 'sample_name';
          return {
            skip,
            limit,
            sort_by: validSortBy,
            sort_order: (sortOrder || 'asc') as 'asc' | 'desc',
            filters: Object.keys(filters).length > 0 ? filters : null,
          };
        },
      ),
    [orgId, studyId],
  );

  // Use shared hook for state and handlers
  const {
    isCollapsed,
    setIsCollapsed,
    pageSize,
    totalCount,
    handleTogglePanel,
    tableContainerClasses,
    defaultColDef,
    handleGridReady,
    handleFirstDataRendered,
    handleSortChanged,
    handleColumnVisible,
    handleFilterChanged,
    handleModelUpdated,
    handlePaginationChanged,
    handleGridSizeChanged,
  } = useSampleTableGrid<SampleProteoformPublic>({
    panelRef,
    adjacentPanelRef,
    studyId,
    validSortFields: VALID_SORT_FIELDS,
    localStorageKeyFn: localStorageKey,
    paginationPageSizeKeyFn: paginationPageSizeKey,
    defaultPageSize: 25,
    datasource,
  });

  // Expose imperative method for parent to update collapsed state
  useImperativeHandle(ref, () => ({
    updateCollapsed: (collapsed: boolean) => setIsCollapsed(collapsed),
  }));

  // Icon logic (unique to this component)
  const Icon = !isCollapsed ? ChevronDown : ChevronUp;

  const proteoformColDefs: ColDef<SampleProteoformPublic>[] = useMemo(
    () => [
      ...createSharedSampleColumns<SampleProteoformPublic>(t),
      {
        cellClass: styles.tableCell,
        field: 'proteoform_name',
        headerName: t('columnHeaders.proteoform'),
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
      },
      {
        cellClass: tableStyles.tableCellCenter,
        field: 'proteoform_abundance',
        headerName: t('columnHeaders.abundance'),
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('proteoform_abundance'),
        valueFormatter: ({ value }) => value?.toLocaleString() || PLACEHOLDER_VALUE,
        resizable: true,
      },
      {
        cellClass: styles.tableCell,
        field: 'created_at',
        headerName: t('columnHeaders.createdAt'),
        sortable: false,
        filter: 'agDateColumnFilter',
        filterParams: DATE_FILTER_PARAMS,
        valueFormatter: ({ value }) => formatDate(value),
        resizable: true,
      },
    ],
    [t, formatDate],
  );

  // Stable double-click handler for title row
  const handleTitleRowDoubleClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target;

      if (target instanceof Element && target.closest('button')) return;
      handleTogglePanel();
    },
    [handleTogglePanel],
  );

  return (
    <Stack
      className={clsx(irStyles.proteoformsTableContainer, getGridItemClassName())}
      fullHeight
      fullWidth
      gap={4}
      orientation="vertical"
    >
      <Stack
        alignItems="center"
        className={irStyles.proteoformsTitleRow}
        justifyContent="space-between"
        orientation="horizontal"
        onDoubleClick={handleTitleRowDoubleClick}
        style={{ cursor: 'pointer' }}
      >
        <Stack gap={4} orientation="horizontal">
          <Text as="h3">{t('proteoforms.title')}</Text>
          <Badge size="sm" variant="info">
            {totalCount === null ? (
              <Loading className={styles.isRefetching} type="inline" />
            ) : (
              totalCount.toLocaleString()
            )}
          </Badge>
        </Stack>

        <Button
          aria-label={isCollapsed ? t('toggle.expand', { ns: 'sidebar' }) : t('toggle.collapse', { ns: 'sidebar' })}
          data-testid={'toggle-expanded'}
          size="sm"
          onPress={handleTogglePanel}
          visual="unstyled"
        >
          <Icon size={16} />
        </Button>
      </Stack>

      <Table
        columnDefs={proteoformColDefs}
        container={{ ...tableContainerClasses, ref: containerRef }}
        defaultColDef={defaultColDef}
        domLayout="normal"
        enableAdvancedFilter={false}
        enableResponsiveResize={true}
        gridOptions={gridOptions}
        onGridReady={handleGridReady}
        onFirstDataRendered={handleFirstDataRendered}
        onSortChanged={handleSortChanged}
        onColumnVisible={handleColumnVisible}
        onFilterChanged={handleFilterChanged}
        onModelUpdated={handleModelUpdated}
        onPaginationChanged={handlePaginationChanged}
        onGridSizeChanged={handleGridSizeChanged}
        pagination
        paginationPageSize={pageSize}
        paginationPageSizeSelector={[10, 25, 50, 100]}
        ref={gridRef}
        resizeDebounceMs={150}
        rowModelType="serverSide"
        tableId="sample-proteoforms-table"
      />
    </Stack>
  );
});
